// ─────────────────────────────────────────────────────────────────────────────
// Mock Dashboard Data — matches all dashboard API response shapes
// Used in: Dashboard.jsx
// Tuned to look polished in marketing screenshots — current month is May 2026.
// ─────────────────────────────────────────────────────────────────────────────

// API_PATHS.DASHBOARD_COUNTS
export const mockDashboardCounts = {
  rescode: 1,
  message: "Dashboard counts fetched successfully",
  data: {
    todayRevenue: 42500,
    todaySessions: 38,
    todaySessionsBreakdown: { done: 20, missed: 3, off: 0, pending: 15 },
    totalAppointments: 18,
    newPatients: 7,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// API_PATHS.DASHBOARD_BIRTHDAY
// "Birthdays This Month" — May 2026
// ─────────────────────────────────────────────────────────────────────────────

export const mockUpcomingBirthdays = {
  rescode: 1,
  message: "Upcoming birthdays fetched successfully",
  data: {
    upcomingBirthdays: [
      {
        id: 101,
        name: "Aryan Sharma",
        birthday: "2018-05-18",
        profile_image: null,
        role: "Patient",
        phone: "9876543210",
        age: 8,
      },
      {
        id: 42,
        name: "Dr. Neha Agarwal",
        birthday: "1988-05-20",
        profile_image: null,
        role: "Staff",
        phone: "9123456789",
        age: 38,
      },
      {
        id: 102,
        name: "Sneha Verma",
        birthday: "2019-05-22",
        profile_image: null,
        role: "Patient",
        phone: "9988776655",
        age: 7,
      },
      {
        id: 43,
        name: "Vikram Khatri",
        birthday: "1993-05-25",
        profile_image: null,
        role: "Staff",
        phone: null,
        age: 33,
      },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// API_PATHS.DASHBOARD_OVERVIEW_GRAPH
// Last-12-months rolling revenue trend (Jan–Dec of 2026), peaking in current
// active months — looks like a healthy growing practice.
// ─────────────────────────────────────────────────────────────────────────────

export const mockOverviewGraph = {
  rescode: 1,
  message: "Overview graph data fetched successfully",
  data: {
    TotalPaid: 540000,
    Payments: [
      28000, // Jan
      32000, // Feb
      38000, // Mar
      45000, // Apr
      52000, // May (current month)
      48000, // Jun
      54000, // Jul
      58000, // Aug
      51000, // Sep
      60000, // Oct
      42000, // Nov
      32000, // Dec
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// API_PATHS.DASHBOARD_MEMBERS_GRAPH
// New patient registrations per month — steady growth, peaking mid-year.
// ─────────────────────────────────────────────────────────────────────────────

export const mockMembersGraph = {
  rescode: 1,
  message: "Members graph data fetched successfully",
  data: {
    TotalMembers: 184,
    Members: [
      11, // Jan
      14, // Feb
      17, // Mar
      19, // Apr
      22, // May (current — strongest)
      18, // Jun
      16, // Jul
      19, // Aug
      14, // Sep
      15, // Oct
      11, // Nov
      8,  // Dec
    ],
    // Patients marked inactive that month (BR-PAT-1), and the roster left standing
    // at each month end. Kept arithmetically consistent with the two above —
    // active[m] = active[m-1] + joined[m] − left[m] + rejoined[m], off a roster of
    // 120 carried in from the previous year — so the mock dashboard cannot show a
    // shape the real endpoint could never produce.
    TotalLeft: 106,
    Left: [6, 7, 9, 8, 10, 9, 11, 10, 12, 9, 8, 7],
    TotalRejoined: 0,
    Rejoined: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    Active: [125, 132, 140, 151, 163, 172, 177, 186, 188, 194, 197, 198],
    ActiveNow: 198,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Mock Active Patients — therapy-wise breakdown for the donut chart
// ─────────────────────────────────────────────────────────────────────────────

export const mockActivePatients = {
  total: 184,
  therapies: [
    { name: "Speech Therapy", count: 52, color: "#7A6EFE", service_id: 1 },
    { name: "Occupational Therapy", count: 41, color: "#FFA901", service_id: 2 },
    { name: "Physiotherapy", count: 28, color: "#FC4B6C", service_id: 3 },
    { name: "Special Education", count: 24, color: "#24A8FA", service_id: 4 },
    { name: "Applied Behavioral (ABA)", count: 22, color: "#2CA538", service_id: 5 },
    { name: "Art & Craft Therapy", count: 17, color: "#FF6D53", service_id: 6 },
  ],
};

// API_PATHS.DASHBOARD_ACTIVE_PATIENTS — patients behind one donut segment.
// Demo mode only needs a believable sample, so the same roster is reused for
// every segment, trimmed to that segment's count.
const mockSegmentRoster = [
  { id: 101, name: "Aryan Sharma", phone: "9876543210" },
  { id: 102, name: "Aanya Sharma", phone: "9876543211" },
  { id: 103, name: "Kabir Singh", phone: "9876543212" },
  { id: 104, name: "Sneha Verma", phone: "9876543213" },
  { id: 105, name: "Myra Kapoor", phone: "9876543214" },
  { id: 106, name: "Rohan Gupta", phone: "9876543215" },
  { id: 107, name: "Ishaan Bhatia", phone: "9876543216" },
  { id: 108, name: "Diya Chopra", phone: "9876543217" },
  { id: 109, name: "Veer Malhotra", phone: "9876543218" },
  { id: 110, name: "Riya Saxena", phone: "9876543219" },
];

export const mockActivePatientsBySegment = (body = {}) => {
  const segment = mockActivePatients.therapies.find((t) => t.name === body.name);
  const count = Math.min(segment?.count ?? 6, mockSegmentRoster.length);
  return {
    rescode: 1,
    message: "Patients fetched successfully",
    data: { patients: mockSegmentRoster.slice(0, count) },
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Mock Waiting List — current week (May 2026)
// ─────────────────────────────────────────────────────────────────────────────

export const mockWaitingList = [
  {
    id: 1,
    name: "Ayaan Khan",
    initials: "AK",
    bgColor: "#EDE9FF",
    textColor: "#7A6EFE",
    mobile: "9876512340",
    date: "14 May, 2026",
    age: 5,
    gender: "Male",
    department: "Speech Therapy",
  },
  {
    id: 2,
    name: "Diya Kapoor",
    initials: "DK",
    bgColor: "#FFF3D6",
    textColor: "#FFA901",
    mobile: "9823145670",
    date: "13 May, 2026",
    age: 4,
    gender: "Female",
    department: "Occupational Therapy",
  },
  {
    id: 3,
    name: "Om Rathore",
    initials: "OR",
    bgColor: "#FFE8EC",
    textColor: "#FC4B6C",
    mobile: "9701234567",
    date: "12 May, 2026",
    age: 7,
    gender: "Male",
    department: "Physiotherapy",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Mock Patient Fees — recent outstanding dues
// ─────────────────────────────────────────────────────────────────────────────

export const mockPatientFees = [
  {
    patient_id: "101",
    patient_name: "Aryan Sharma",
    patient_phone: "9610100479",
    initials: "AS",
    total_due: 3500,
    invoice_count: 2,
    oldest_invoice: "2026-04-08",
    pending_months: ["Apr", "May"],
  },
  {
    patient_id: "104",
    patient_name: "Pooja Mishra",
    patient_phone: "8302285882",
    initials: "PM",
    total_due: 2000,
    invoice_count: 1,
    oldest_invoice: "2026-05-01",
    pending_months: ["May"],
  },
  {
    patient_id: "108",
    patient_name: "Ishaan Rawat",
    patient_phone: "9829260370",
    initials: "IR",
    total_due: 5200,
    invoice_count: 3,
    oldest_invoice: "2026-03-12",
    pending_months: ["Mar", "Apr", "May"],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Mock Support Requests
// ─────────────────────────────────────────────────────────────────────────────

export const mockSupportRequests = [
  {
    id: 1,
    name: "Rajesh Sharma",
    message: "Requested rescheduling of session on 17 May",
    time: "2 hours ago",
    status: "Pending",
  },
  {
    id: 2,
    name: "Anita Verma",
    message: "Query about invoice #1034 payment",
    time: "Yesterday",
    status: "In Review",
  },
  {
    id: 3,
    name: "Chirag Patel",
    message: "Feedback on therapy progress report",
    time: "2 days ago",
    status: "Resolved",
  },
];
